import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_KEYS } from '@/lib/constants';

type Theme = 'light' | 'dark' | 'system';

/** Email-notification toggles. No backend yet — persisted client-side (legacy parity). */
export type NotificationKey =
  | 'platformUpdates'
  | 'messages'
  | 'verificationCaseUpdates'
  | 'supportTicketUpdates';

export type NotificationPrefs = Record<NotificationKey, boolean>;

const DEFAULT_NOTIFICATIONS: NotificationPrefs = {
  platformUpdates: true,
  messages: true,
  verificationCaseUpdates: true,
  supportTicketUpdates: true,
};

interface UIState {
  theme: Theme;
  locale: string;
  sidebarCollapsed: boolean;
  /** Two-step verification preference. No backend endpoint yet — local only. */
  twoFactorEnabled: boolean;
  notifications: NotificationPrefs;
  setTheme: (theme: Theme) => void;
  setLocale: (locale: string) => void;
  toggleSidebar: () => void;
  setSidebar: (collapsed: boolean) => void;
  setTwoFactor: (enabled: boolean) => void;
  setNotification: (key: NotificationKey, value: boolean) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      theme: 'system',
      locale: 'en',
      sidebarCollapsed: false,
      twoFactorEnabled: false,
      notifications: DEFAULT_NOTIFICATIONS,
      setTheme: (theme) => {
        applyThemeClass(theme);
        set({ theme });
      },
      setLocale: (locale) => set({ locale }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebar: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setTwoFactor: (twoFactorEnabled) => set({ twoFactorEnabled }),
      setNotification: (key, value) =>
        set((s) => ({ notifications: { ...s.notifications, [key]: value } })),
    }),
    {
      name: 'tally:ui',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export function applyThemeClass(theme: Theme) {
  const root = document.documentElement;
  const resolved =
    theme === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : theme;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
}

export function hydrateTheme() {
  const raw = localStorage.getItem('tally:ui');
  let theme: Theme = 'system';
  try {
    const parsed = raw ? (JSON.parse(raw) as { state?: { theme?: Theme } }) : null;
    if (parsed?.state?.theme) theme = parsed.state.theme;
  } catch {
    /* ignore */
  }
  applyThemeClass(theme);

  // Keep system theme reactive
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', () => {
    if (useUIStore.getState().theme === 'system') applyThemeClass('system');
  });
}

export const RTL_LOCALES = new Set(['ar', 'he', 'fa', 'ur']);

export function applyDirection(locale: string) {
  document.documentElement.dir = RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
  document.documentElement.lang = locale;
}

// Reuse storage key for clarity
void STORAGE_KEYS;
